/**
 * Pure form helpers for the My Business (Studio) settings tabs.
 *
 * These mirror the normalize / validate / submit logic that
 * `components/forms/BusinessInfoForm.js` and
 * `components/forms/AdvancedSettingsFields.js` use for the onboarding flow, but
 * live here so the redesigned Settings tabs — which edit fields *inline* rather
 * than in a stacked form — share one source of truth for the business rules
 * (phone splitting, address mapping, booking-horizon coercion, field labels).
 * Behavior is intentionally identical to those forms so the two surfaces stay
 * in sync.
 */

import {
  BadgeSettings,
  badgeSettingsFrom,
  badgeSettingsPayload,
} from "./badgeSettings";
import {
  DEFAULT_SLOT_INTERVAL_MINS,
  SLOT_INTERVAL_LABELS,
  US_TIMEZONES,
} from "./formConstants";

const TIMEZONE_LABELS: Record<string, string> = Object.fromEntries(
  US_TIMEZONES.map((tz: SelectOption) => [tz.value, tz.label]),
);

/** IANA timezone value → its friendly label (falls back to the raw value). */
export const formatTimezone = (timezone?: string | null): string =>
  timezone ? TIMEZONE_LABELS[timezone] || timezone : "N/A";

export interface SelectOption {
  value: string;
  label: string;
}

export const BUSINESS_VERTICAL_OPTIONS: SelectOption[] = [
  { value: "hair", label: "Hair Salon" },
  { value: "nail", label: "Nail Salon" },
  { value: "barber", label: "Barbershop" },
  { value: "aesthetician", label: "Aesthetician" },
  { value: "med_spa", label: "Med Spa" },
];

export const STAFFING_MODEL_OPTIONS: SelectOption[] = [
  { value: "full_service_w2", label: "Full-Service (W2)" },
  { value: "booth_renter_1099", label: "Booth Renter (1099)" },
  { value: "hybrid", label: "Hybrid" },
];

const labelMap = (options: SelectOption[]): Record<string, string> =>
  Object.fromEntries(options.map((o) => [o.value, o.label]));

export const BUSINESS_VERTICAL_LABELS = labelMap(BUSINESS_VERTICAL_OPTIONS);
export const STAFFING_MODEL_LABELS = labelMap(STAFFING_MODEL_OPTIONS);

const PHONE_REGEX = /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/;

export const isValidPhoneNumber = (phone: string): boolean =>
  PHONE_REGEX.test(phone || "");

/** Salon `area_code`/`phone_number` → "(212) 555-1234" (or "" when missing). */
export const formatPhoneForDisplay = (
  areaCode?: string,
  phoneNumber?: string,
): string => {
  if (!areaCode || !phoneNumber) return "";
  return `(${areaCode}) ${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3)}`;
};

// ── Business Information ────────────────────────────────────────

export interface BusinessInfoValues {
  salon_name: string;
  legal_business_name: string;
  phone: string;
  streetAddress1: string;
  streetAddress2: string;
  city: string;
  state: string;
  zip_code: string;
  timezone: string;
  google_review_link: string;
  business_vertical: string;
  staffing_model: string;
}

/** Salon record → editable Business Information form values. */
export const normalizeBusinessInfo = (data: any): BusinessInfoValues => ({
  salon_name: data?.salon_name || "",
  legal_business_name: data?.legal_business_name || "",
  phone: formatPhoneForDisplay(data?.area_code, data?.phone_number),
  streetAddress1: data?.street_address_1 || "",
  streetAddress2: data?.street_address_2 || "",
  city: data?.city || "",
  state: data?.state || "",
  zip_code: data?.zip_code || "",
  timezone: data?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
  google_review_link: data?.google_review_link || "",
  business_vertical: data?.business_vertical || "",
  staffing_model: data?.staffing_model || "",
});

export const validateBusinessInfo = (
  values: BusinessInfoValues,
): Record<string, string> => {
  const errors: Record<string, string> = {};
  if (!values.salon_name?.trim()) {
    errors.salon_name = "Business name is required";
  }
  if (!values.phone?.trim()) {
    errors.phone = "Phone number is required";
  } else if (!isValidPhoneNumber(values.phone)) {
    errors.phone = "Please enter a valid phone number (e.g., 123-456-7890)";
  }
  if (!values.streetAddress1?.trim()) {
    errors.streetAddress1 = "Street address is required";
  }
  if (!values.city?.trim()) {
    errors.city = "City is required";
  }
  if (!values.state?.trim()) {
    errors.state = "State is required";
  }
  if (!values.zip_code?.trim()) {
    errors.zip_code = "Zip code is required";
  }
  if (!values.timezone?.trim()) {
    errors.timezone = "Timezone is required";
  }
  if (values.google_review_link?.trim()) {
    if (values.google_review_link.length > 1024) {
      errors.google_review_link =
        "Google review link must be less than 1024 characters";
    }
    if (!values.google_review_link.includes("https://g.page/r")) {
      errors.google_review_link =
        'Link is not a valid shortened Google Review link.  Please navigate to your Google Business Profile and click "Ask for reviews" to get your link';
    }
  }
  return errors;
};

/** Business Information form values → partial salon payload for PUT /business-locations. */
export const buildBusinessInfoPayload = (
  values: BusinessInfoValues,
): Record<string, any> => {
  const payload: Record<string, any> = {
    salon_name: values.salon_name,
    legal_business_name: values.legal_business_name,
    city: values.city,
    state: values.state,
    zip_code: values.zip_code,
    timezone: values.timezone,
    google_review_link: values.google_review_link,
    business_vertical: values.business_vertical,
    staffing_model: values.staffing_model,
    street_address_1: values.streetAddress1,
    street_address_2: values.streetAddress2 || "",
  };

  const phoneMatch = (values.phone || "").match(PHONE_REGEX);
  if (phoneMatch) {
    payload.area_code = phoneMatch[1];
    payload.phone_number = `${phoneMatch[2]}${phoneMatch[3]}`;
  }

  return payload;
};

// ── Advanced Settings ───────────────────────────────────────────

export interface AdvancedSettingsValues extends BadgeSettings {
  booking_delay_mins: number;
  availability_slot_interval_mins: number;
  customer_booking_horizon_days: number | null;
  appts_need_accepting: boolean;
  appts_need_confirmation: boolean;
}

/** Salon record → editable Advanced Settings form values. */
export const normalizeAdvancedSettings = (
  data: any,
): AdvancedSettingsValues => ({
  booking_delay_mins: data?.booking_delay_mins || 60,
  availability_slot_interval_mins:
    data?.availability_slot_interval_mins || DEFAULT_SLOT_INTERVAL_MINS,
  customer_booking_horizon_days: data?.customer_booking_horizon_days ?? null,
  appts_need_accepting: data?.appts_need_accepting || false,
  appts_need_confirmation: data?.appts_need_confirmation || false,
  // Calendar badge toggles: missing = shown.
  ...badgeSettingsFrom(data),
});

/** Coerce a raw horizon input to a valid 0–365 integer, else null (unlimited). */
export const coerceBookingHorizon = (raw: unknown): number | null => {
  const n = Number(raw);
  if (
    raw === null ||
    raw === undefined ||
    raw === "" ||
    !Number.isInteger(n) ||
    n < 0 ||
    n > 365
  ) {
    return null;
  }
  return n;
};

export const validateAdvancedSettings = (
  values: AdvancedSettingsValues,
): Record<string, string> => {
  const errors: Record<string, string> = {};
  const horizon = values.customer_booking_horizon_days;
  if (horizon !== null && horizon !== undefined && (horizon as any) !== "") {
    const n = Number(horizon);
    if (!Number.isInteger(n) || n < 0 || n > 365) {
      errors.customer_booking_horizon_days =
        "Booking horizon must be between 0 and 365 days (leave blank for unlimited)";
    }
  }
  return errors;
};

/** Advanced Settings form values → partial salon payload for PUT /business-locations. */
export const buildAdvancedSettingsPayload = (
  values: AdvancedSettingsValues,
): Record<string, any> => ({
  booking_delay_mins: values.booking_delay_mins,
  availability_slot_interval_mins: values.availability_slot_interval_mins,
  customer_booking_horizon_days: coerceBookingHorizon(
    values.customer_booking_horizon_days,
  ),
  appts_need_accepting: values.appts_need_accepting,
  appts_need_confirmation: values.appts_need_confirmation,
  ...badgeSettingsPayload(values),
});

// ── Read-view formatting helpers ────────────────────────────────

export const formatBusinessVertical = (vertical?: string | null): string =>
  vertical ? BUSINESS_VERTICAL_LABELS[vertical] || vertical : "N/A";

export const formatStaffingModel = (model?: string | null): string =>
  model ? STAFFING_MODEL_LABELS[model] || model : "N/A";

export const formatSlotInterval = (mins?: number | null): string =>
  SLOT_INTERVAL_LABELS[mins ?? DEFAULT_SLOT_INTERVAL_MINS] ||
  SLOT_INTERVAL_LABELS[DEFAULT_SLOT_INTERVAL_MINS];

export const formatBookingHorizon = (days?: number | null): string => {
  if (typeof days === "number") {
    return days === 0
      ? "0 days (same-day only, likely misconfigured)"
      : `${days} days`;
  }
  return "Unlimited";
};
